import * as React from "react";
import { SearchBar, type SearchBarFormData } from "@fm/ui";
import { categories, inventory } from "@fm/api";
import type { CategoryWithDetails } from "@fm/api";
import type { VehicleCategorySelection } from "@fm/ui";
import type { ApiClient } from "@fm/api/types";
import { getApiBaseUrl } from "../lib/config";
import { toDateString } from "@fm/ui";

export interface SearchBarContainerProps {
  apiKey: string;
  fleetId?: string;
  apiBaseUrl?: string;
  theme?: "light" | "dark";
  layout?: "auto" | "horizontal" | "vertical";
  placeholder?: string;
  primaryColor?: string;
  onSearch?: (data: SearchBarFormData & { fleet_id?: string }) => void;
  onChange?: (data: SearchBarFormData) => void;
  initialData?: Partial<SearchBarFormData>;
  showVehicleSelector?: boolean;
  className?: string;
  disabled?: boolean;
}

const defaultFormData: SearchBarFormData = {
  pickUp: "",
  return: "",
  adults: 2,
  children: 0,
  infants: 0,
  pets: 0,
};

export function SearchBarContainer({
  apiKey,
  fleetId,
  apiBaseUrl,
  theme = "light",
  layout = "auto",
  placeholder = "Search vehicles",
  primaryColor,
  onSearch,
  onChange,
  initialData = {},
  showVehicleSelector = true,
  className,
  disabled = false,
}: SearchBarContainerProps) {
  const [formData, setFormData] = React.useState<SearchBarFormData>({
    ...defaultFormData,
    ...initialData,
  });
  const [categoriesList, setCategoriesList] = React.useState<
    CategoryWithDetails[]
  >([]);
  const [uncategorizedVehicles, setUncategorizedVehicles] = React.useState<
    Array<{ id: string; name: string; make?: string; model?: string }>
  >([]);
  const [vehicleCategorySelection, setVehicleCategorySelection] =
    React.useState<VehicleCategorySelection | null>(null);
  const [unavailableDates, setUnavailableDates] = React.useState<Set<string>>(
    new Set(),
  );
  const [isLoading, setIsLoading] = React.useState(false);
  const [errors, setErrors] = React.useState<Partial<SearchBarFormData>>({});
  const [isMobile, setIsMobile] = React.useState(false);

  // Create API client
  const apiClient = React.useMemo<ApiClient>(
    () => ({
      // TODO: assign fleet id and context to the api key
      apiKey,
      baseUrl: getApiBaseUrl(apiBaseUrl),
    }),
    [apiKey, apiBaseUrl],
  );

  // Detect screen size for responsive layout
  React.useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 1140); // md breakpoint
    };

    // Check on mount
    checkScreenSize();

    // Add event listener
    window.addEventListener("resize", checkScreenSize);

    // Cleanup
    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Load categories and uncategorized vehicles on mount
  React.useEffect(() => {
    const loadCategoriesAndVehicles = async () => {
      try {
        if (!fleetId) {
          setCategoriesList([]);
          return;
        }

        // Get all categories
        const categoriesResponse = await categories.list(apiClient, fleetId);
        const categoriesData = categoriesResponse.data;

        // Get detailed info for each category including vehicles
        const categoriesWithDetails = await Promise.all(
          categoriesData.map(async (category) => {
            try {
              const detailResponse = await categories.get(
                apiClient,
                fleetId,
                category.id,
              );
              return detailResponse.data;
            } catch (error) {
              console.error(
                `Failed to fetch details for category ${category.id}:`,
                error,
              );
              // Return basic category info with empty vehicles array if detail fetch fails
              return {
                ...category,
                vehicles: [],
                photos: [],
              } as CategoryWithDetails;
            }
          }),
        );

        setCategoriesList(categoriesWithDetails);

        const categorizedVehicleIds = new Set<string>();
        categoriesWithDetails.forEach((category) => {
          (category.vehicles || []).forEach((vehicle) => {
            categorizedVehicleIds.add(vehicle.id);
          });
        });

        try {
          const today = new Date();
          const tomorrow = new Date();
          tomorrow.setDate(today.getDate() + 1);

          const fromDate = toDateString(today) as string;
          const toDate = toDateString(tomorrow) as string;

          const inventoryResponse = await inventory.getGrid(apiClient, {
            from_date: fromDate,
            to_date: toDate,
          });

          if (inventoryResponse.data?.vehicles) {
            const uncategorized = inventoryResponse.data.vehicles
              .filter((vehicle) => !categorizedVehicleIds.has(vehicle.id))
              .map((vehicle) => ({
                id: vehicle.id,
                name: vehicle.name,
              }));

            setUncategorizedVehicles(uncategorized);
          }
        } catch (inventoryError) {
          console.error(
            "Failed to load inventory for uncategorized vehicles:",
            inventoryError,
          );
        }
      } catch (error) {
        console.error("Failed to load categories:", error);
        setCategoriesList([]);
      }
    };

    loadCategoriesAndVehicles();
  }, [apiClient, fleetId]);

  // Initialize vehicle selection from initial data
  React.useEffect(() => {
    if (
      categoriesList.length > 0 &&
      (initialData.categoryId || initialData.vehicleId)
    ) {
      if (initialData.categoryId) {
        // Find category and set selection
        const category = categoriesList.find(
          (cat) => cat.id === initialData.categoryId,
        );
        if (category) {
          setVehicleCategorySelection({
            type: "category",
            categoryId: category.id,
            categoryName: category.name,
          });
        }
      } else if (initialData.vehicleId) {
        // Find vehicle and set selection
        for (const category of categoriesList) {
          const vehicle = category.vehicles?.find(
            (v) => v.id === initialData.vehicleId,
          );
          if (vehicle) {
            setVehicleCategorySelection({
              type: "vehicle",
              categoryId: category.id,
              categoryName: category.name,
              vehicleId: vehicle.id,
              vehicleName: vehicle.name,
            });
            break;
          }
        }
      }
    }
  }, [categoriesList, initialData.categoryId, initialData.vehicleId]);

  // Load unavailable dates when vehicle/category selection changes
  React.useEffect(() => {
    const loadUnavailableDates = async () => {
      if (!vehicleCategorySelection) {
        setUnavailableDates(new Set());
        return;
      }

      try {
        const today = new Date();
        const oneYearFromNow = new Date();
        oneYearFromNow.setFullYear(today.getFullYear() + 1);

        const fromDate = toDateString(today);
        const toDate = toDateString(oneYearFromNow);

        const isCategory = vehicleCategorySelection.type === "category";
        const vehicleId: string | undefined = isCategory
          ? undefined
          : vehicleCategorySelection.vehicleId;
        const categoryId: string | undefined = isCategory
          ? vehicleCategorySelection.categoryId
          : undefined;

        const params: any = {
          from_date: fromDate,
          to_date: toDate,
        };
        if (vehicleId) {
          params.vehicle_id = vehicleId;
        }
        if (categoryId) {
          params.category_id = categoryId;
        }

        const response = await inventory.getUnavailableDateRanges(
          apiClient,
          params,
        );

        const ranges = response.data;

        // Convert ranges to individual dates
        const unavailable = new Set<string>();
        ranges.forEach((range) => {
          const startDate = new Date(range.start_date + "T00:00:00");
          const endDate = new Date(range.end_date + "T00:00:00");

          for (
            let d = new Date(startDate);
            d <= endDate;
            d.setDate(d.getDate() + 1)
          ) {
            const dateStr = toDateString(d);
            if (dateStr) unavailable.add(dateStr);
          }
        });

        setUnavailableDates(unavailable);
      } catch (error) {
        console.error("Failed to load unavailable dates:", error);
        setUnavailableDates(new Set());
      }
    };

    loadUnavailableDates();
  }, [vehicleCategorySelection, apiClient]);

  const updateFormData = (updates: Partial<SearchBarFormData>) => {
    const newFormData = { ...formData, ...updates };
    setFormData(newFormData);

    // Clear errors when user makes changes
    if (Object.keys(errors).length > 0) {
      setErrors({});
    }

    // Call onChange callback
    onChange?.(newFormData);
  };

  const handleVehicleCategoryChange = (
    selection: VehicleCategorySelection | null,
  ) => {
    setVehicleCategorySelection(selection);

    if (selection) {
      if (selection.type === "category") {
        updateFormData({
          categoryId: selection.categoryId,
          vehicleId: undefined,
        });
      } else if (selection.type === "vehicle") {
        updateFormData({
          categoryId: selection.categoryId,
          vehicleId: selection.vehicleId,
        });
      }
    } else {
      updateFormData({ categoryId: undefined, vehicleId: undefined });
    }
  };

  // Date range handler
  const handleDateRangeChange = (range: [string, string] | null) => {
    if (range && range[0] && range[1]) {
      updateFormData({ pickUp: range[0], return: range[1] });
    } else {
      updateFormData({ pickUp: "", return: "" });
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.pickUp) {
      newErrors.pickUp = "Check-in date is required";
    }

    if (!formData.return) {
      newErrors.return = "Check-out date is required";
    }

    if (
      formData.pickUp &&
      formData.return &&
      formData.pickUp >= formData.return
    ) {
      newErrors.return = "Check-out date must be after check-in date";
    }

    if (formData.adults < 1) {
      newErrors.adults = "At least 1 adult is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSearch = async (data: SearchBarFormData) => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      // Call onSearch callback with fleet_id included
      onSearch?.({ ...data, fleet_id: fleetId });
    } catch (error) {
      console.error("Search failed:", error);
      setErrors({ pickUp: "Search failed. Please try again." });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <SearchBar
      formData={formData}
      categoriesList={categoriesList}
      vehicleCategorySelection={vehicleCategorySelection}
      unavailableDates={unavailableDates}
      isLoading={isLoading}
      errors={errors}
      isMobile={isMobile}
      theme={theme}
      layout={layout}
      placeholder={placeholder}
      primaryColor={primaryColor}
      onSearch={handleSearch}
      onChange={updateFormData}
      onVehicleCategoryChange={handleVehicleCategoryChange}
      onDateRangeChange={handleDateRangeChange}
      showVehicleSelector={showVehicleSelector}
      className={className}
      disabled={disabled}
      uncategorizedVehicles={uncategorizedVehicles}
    />
  );
}
